import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import usePlayerManifest from "../hooks/usePlayerManifest";
import PlayerLayout from "../components/PlayerLayout";
import storage from '../nativeStorage';
import api from '../api';

export default function PlayerHcpVerify() {
  usePlayerManifest();
  const navigate = useNavigate();
  const token = storage.getItem("player_token");
  const myName = storage.getItem("player_name");

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [events, setEvents] = useState([]);
  const [eventData, setEventData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState({ msg: "", ok: true });
  const [verifiedIds, setVerifiedIds] = useState({});

  const showToast = (msg, ok = true) => {
    setToast({ msg, ok });
    setTimeout(() => setToast({ msg: "", ok: true }), 3500);
  };

  useEffect(() => {
    if (!token) { navigate("/player/login"); return; }
    api.get("/player/hcp-events", { headers: { Authorization: "Bearer " + token } })
      .then(res => setEvents(Array.isArray(res.data) ? res.data : []))
      .catch(err => setError(err.response?.data?.error || "Could not load competitions"))
      .finally(() => setLoading(false));
  }, []);

  const pickEvent = (ev) => {
    setLoading(true); setError("");
    api.get("/player/hcp-partners/" + ev.id, { headers: { Authorization: "Bearer " + token } })
      .then(res => { setEventData(res.data); setStep(2); })
      .catch(err => setError(err.response?.data?.error || "Network error"))
      .finally(() => setLoading(false));
  };

  const verifyPartner = async (verifiedPlayerId) => {
    setBusy(true);
    try {
      await api.post("/player/hcp-verify",
        { event_id: eventData.event.id, verified_player_id: verifiedPlayerId },
        { headers: { Authorization: "Bearer " + token } });
      setVerifiedIds(prev => ({ ...prev, [verifiedPlayerId]: true }));
      showToast("Score verified successfully!", true);
    } catch(e) { showToast(e.response?.data?.error || "Failed to verify", false); }
    finally { setBusy(false); }
  };

  const btn = { display: "block", width: "100%", padding: "14px", marginBottom: "10px", border: "2px solid #d1d5db", borderRadius: "10px", background: "#f9fafb", textAlign: "left", fontSize: "15px", cursor: "pointer", fontWeight: "600", color: "#111827" };
  const btnDone = { ...btn, background: "#f0fdf4", borderColor: "#16a34a", cursor: "default", color: "#15803d" };

  const instructions = (
    <>
      <h2 style={{ fontSize: "14px", fontWeight: "700", color: "#14532d", marginBottom: "8px" }}>Verifying a card</h2>
      <div style={{ fontSize: "12px", color: "#374151", lineHeight: "2.2" }}>
        Pick the competition date, then tap each player your organiser assigned you to mark. Once verified, a ✅ badge appears next to their score on the results page.
      </div>
    </>
  );

  return (
    <PlayerLayout instructions={instructions}>
      <h1 style={{ fontSize: "20px", fontWeight: "800", color: "#14532d", marginBottom: "4px" }}>📋 HCP Score Verification</h1>
      <p style={{ fontSize: "13px", color: "#6b7280", marginBottom: "20px" }}>Signed in as {myName}</p>

      <div style={{ maxWidth: "500px" }}>
        <div style={{ background: "white", borderRadius: "12px", boxShadow: "0 2px 8px rgba(0,0,0,0.06)", padding: "20px" }}>
          {loading && <div style={{ textAlign: "center", color: "#6b7280", padding: "30px 0" }}>Loading...</div>}
          {error && <div style={{ background: "#fef2f2", color: "#b91c1c", padding: "10px 14px", borderRadius: "8px", fontSize: "13px", marginBottom: "14px" }}>{error}</div>}

          {!loading && step === 1 && (
            <>
              <div style={{ fontSize: "14px", fontWeight: "700", color: "#14532d", marginBottom: "4px" }}>Select a competition date</div>
              <div style={{ fontSize: "12px", color: "#6b7280", marginBottom: "16px" }}>Choose the round you want to verify a playing partner's card for:</div>
              {events.length === 0 && (
                <div style={{ color: "#9ca3af", fontSize: "13px", padding: "20px 0", textAlign: "center" }}>
                  No HCP-eligible competitions found for you yet.<br/>
                  <span style={{ fontSize: "11px" }}>Only Singles Stableford, Medal and Stroke Play qualify.</span>
                </div>
              )}
              {events.map(ev => (
                <button key={ev.id} style={btn} onClick={() => pickEvent(ev)}>
                  <div>{ev.date ? new Date(ev.date).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" }) : "Unknown date"}</div>
                  <div style={{ fontSize: "12px", fontWeight: "400", color: "#6b7280", marginTop: "2px" }}>
                    {ev.game_type}{ev.venue ? " — " + ev.venue : ""}
                    {ev.verified_by_player_id && <span style={{ color: "#16a34a", marginLeft: "8px" }}>✅ Verified</span>}
                  </div>
                </button>
              ))}
            </>
          )}

          {!loading && step === 2 && eventData && (
            <>
              <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: "8px", padding: "12px 14px", marginBottom: "16px" }}>
                <div style={{ fontWeight: "700", color: "#14532d", fontSize: "14px" }}>{eventData.event.course_name || "Course not set"}</div>
                <div style={{ fontSize: "12px", color: "#6b7280", marginTop: "2px" }}>
                  {eventData.event.date ? new Date(eventData.event.date).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" }) : ""}
                  {" — "}{eventData.event.game_type}
                </div>
              </div>

              <div style={{ fontSize: "14px", fontWeight: "700", color: "#14532d", marginBottom: "4px" }}>Your assigned player to verify</div>
              <div style={{ fontSize: "12px", color: "#6b7280", marginBottom: "14px" }}>
                Tap a player's name to confirm you have checked their scorecard:
              </div>

              {eventData.partners.length === 0 && (
                <div style={{ color: "#9ca3af", fontSize: "13px", padding: "16px 0", textAlign: "center" }}>
                  No players are assigned to you for verification in this round.<br/>
                  <span style={{ fontSize: "11px" }}>Contact your society organiser if this is incorrect.</span>
                </div>
              )}

              {eventData.partners.map(p => {
                const done = verifiedIds[p.player_id] || !!p.verified_by_player_id;
                return (
                  <button key={p.player_id} disabled={busy || done} style={done ? btnDone : btn}
                    onClick={() => !done && verifyPartner(p.player_id)}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <span>{done ? "✅ " : "☐ "}{p.name}</span>
                      <span style={{ fontSize: "11px", color: done ? "#16a34a" : "#6b7280", fontWeight: "400" }}>
                        {done ? "Verified" : "Tap to verify →"}
                      </span>
                    </div>
                  </button>
                );
              })}

              <button style={{ ...btn, background: "white", color: "#6b7280", fontWeight: "400", marginTop: "8px", border: "1px solid #e5e7eb" }}
                onClick={() => { setStep(1); setEventData(null); setVerifiedIds({}); }}>
                ← Choose a different date
              </button>
            </>
          )}
        </div>
      </div>

      {toast.msg && (
        <div style={{ position: "fixed", bottom: "20px", left: "50%", transform: "translateX(-50%)", background: toast.ok ? "#166534" : "#b91c1c", color: "white", padding: "12px 24px", borderRadius: "10px", fontSize: "14px", fontWeight: "600", boxShadow: "0 4px 16px rgba(0,0,0,0.25)", zIndex: 9999 }}>
          {toast.msg}
        </div>
      )}
    </PlayerLayout>
  );
}